import { queryClient } from '@/lib/queryClient';
import { api } from '@/lib/api';
import { formatNumber, plural, shortUrl } from '@/lib/format';
import { createStore } from '@/lib/store';
import {
  DEFAULT_COUNT,
  type FillField,
  type FillTarget,
  type FillFieldValue,
  type FinderMeta,
  type FinderMode,
  type FinderSearchSummary,
  type LikeTrait,
  type StaffBand,
} from '@shared/finder';

// The Finder's data on the web app side: query keys, fetchers, the drafts of the three search forms
// and the small helpers several panels share.

export const keys = {
  all: ['finder'] as const,
  meta: ['finder', 'meta'] as const,
  list: ['finder', 'searches'] as const,
  search: (id: string) => ['finder', id] as const,
  fill: ['finder', 'fill'] as const,
  recordFill: (type: FillTarget, id: string) => ['finder', 'fill', type, id] as const,
  companyPeople: (id: string) => ['finder', 'key-people', id] as const,
  peopleRun: (id: string) => ['finder', 'people-run', id] as const,
  companyPeopleFill: (companyId: string) => ['finder', 'fill', 'company', companyId, 'people'] as const,
  companies: (q: string, gaps: boolean, fields: string) => ['finder', 'companies', q, gaps, fields] as const,
  people: (q: string, gaps: boolean, fields: string) => ['finder', 'people', q, gaps, fields] as const,
  company: (id: string) => ['finder', 'company', id] as const,
  addPreview: (resultId: string) => ['finder', 'add', resultId] as const,
};

export const metaQuery = { queryKey: keys.meta, queryFn: () => api.get<FinderMeta>('/finder/meta'), staleTime: 30_000 };

/* ---------- drafts of the search forms ---------- */

export interface FiltersDraft {
  industry: string;
  location: string;
  staff: StaffBand | '';
  foundedFrom: string;
  foundedTo: string;
  keywords: string;
  count: number;
  excludeKnown: boolean;
}

export interface Drafts {
  describe: { query: string };
  like: { companyId: string | null; traits: LikeTrait[]; note: string };
  filters: FiltersDraft;
  /** Bumped to move focus into the form, such as after "Edit query". */
  focus: number;
}

export const EMPTY_FILTERS: FiltersDraft = {
  industry: '',
  location: '',
  staff: '',
  foundedFrom: '',
  foundedTo: '',
  keywords: '',
  count: DEFAULT_COUNT,
  excludeKnown: false,
};

export const draftStore = createStore<Drafts>({
  describe: { query: '' },
  like: { companyId: null, traits: ['industry', 'size', 'needs'], note: '' },
  filters: EMPTY_FILTERS,
  focus: 0,
});

/** Puts a search's settings back into its form, for "Edit query". */
export function draftFromSearch(s: { mode: FinderMode; query: string; params: Record<string, unknown> }): void {
  const p = s.params;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const year = (v: unknown) => (typeof v === 'number' ? String(v) : '');
  if (s.mode === 'describe') draftStore.set((d) => ({ describe: { query: s.query }, focus: d.focus + 1 }));
  else if (s.mode === 'like') {
    draftStore.set((d) => ({
      like: {
        companyId: str(p.companyId) || null,
        traits: Array.isArray(p.traits) ? (p.traits as LikeTrait[]) : d.like.traits,
        note: str(p.note),
      },
      focus: d.focus + 1,
    }));
  } else if (s.mode === 'filters') {
    draftStore.set((d) => ({
      filters: {
        industry: str(p.industry),
        location: str(p.location),
        staff: str(p.staff) as StaffBand | '',
        foundedFrom: year(p.foundedFrom),
        foundedTo: year(p.foundedTo),
        keywords: str(p.keywords),
        count: typeof p.count === 'number' ? p.count : DEFAULT_COUNT,
        excludeKnown: p.excludeKnown === true,
      },
      focus: d.focus + 1,
    }));
  }
}

/* ---------- starting searches ---------- */

export type NewSearchBody =
  | { mode: 'describe'; query: string; count: number }
  | { mode: 'like'; companyId: string; traits: LikeTrait[]; note: string; count: number }
  | {
      mode: 'filters';
      industry: string;
      location: string;
      staff: StaffBand | '';
      foundedFrom: number | null;
      foundedTo: number | null;
      keywords: string;
      count: number;
      excludeKnown: boolean;
    };

export async function startSearch(body: NewSearchBody): Promise<FinderSearchSummary> {
  const summary = await api.post<FinderSearchSummary>('/finder/searches', body);
  void queryClient.invalidateQueries({ queryKey: keys.all });
  return summary;
}

/** Starts a fill run. `skipped` counts the people left out because AI could not tell them apart from namesakes. */
export async function startFill(target: FillTarget, ids: string[], fields?: FillField[]): Promise<FinderSearchSummary & { skipped: number }> {
  const summary = await api.post<FinderSearchSummary & { skipped: number }>('/finder/fill/run', { [target === 'person' ? 'personIds' : 'companyIds']: ids, fields });
  void queryClient.invalidateQueries({ queryKey: keys.all });
  return summary;
}

/* ---------- small helpers ---------- */

/** "41 s", "2 min 05 s". */
export function duration(startedAt: string | null, finishedAt: string | null): string | null {
  if (!startedAt || !finishedAt) return null;
  const s = Math.max(0, Math.round((Date.parse(finishedAt) - Date.parse(startedAt)) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${String(s % 60).padStart(2, '0')} s`;
}

/** A steady tint for a company that is not in Clientbook yet, from its name. */
export function hueFor(name: string): number {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.codePointAt(0)!) % 360;
  return h;
}

/** A proposed or current value as the fill rows show it: "empty", a formatted number, or a short address. */
export function fillValueText(field: FillField, v: FillFieldValue | null | undefined): string {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return 'empty';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'number') return field === 'founded' ? String(v) : formatNumber(v);
  return field === 'linkedin' || field === 'website' ? shortUrl(v) : v;
}

/** The error toast for an undo that left accepted values in place, with the first reason. */
export function undoSkippedText(skipped: { reason: string }[]): string {
  return skipped.length === 1 ? skipped[0]!.reason : `${plural(skipped.length, 'value')} could not be undone. ${skipped[0]!.reason}`;
}

export function isLive(status: FinderSearchSummary['status']): boolean {
  return status === 'queued' || status === 'running';
}
